/**
 * Pantalla 3 (docs/FASE-1.md §6): clientes OAuth de un módulo, alcanzados desde `ModuleDetail`.
 * Alta, corrección y rotación de secreto — el secreto se muestra una única vez (`SecretReveal`).
 *
 * Eliminar solo funciona con un cliente que nunca emitió credenciales, y el API lo comprueba: los
 * refresh tokens y los códigos de autorización cuelgan del cliente en cascada, así que borrar uno en
 * uso dejaría sin sesión a todo el que estuviera en ese módulo. Para ese caso se cambia a
 * «Revocado», que el flujo OIDC ya respeta y se puede deshacer. Eliminar existe porque el
 * `clientId` es único: uno registrado por error bloquea su propio nombre.
 */
import type {
  CreateOAuthClientInput,
  OAuthClient,
  OAuthClientStatus,
  OAuthClientType,
  UpdateOAuthClientInput,
} from "@continnua/contracts";
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from "react";

import { clientsApi } from "../api/client";
import { Badge, toneForStatus } from "../components/Badge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { Field } from "../components/Field";
import { Modal } from "../components/Modal";
import { SecretReveal } from "../components/SecretReveal";
import { toast } from "../components/Toast";
import { toDisplayMessage } from "../lib/errors";

function linesToArray(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function CreateClientModal({
  moduleCode,
  onClose,
  onCreated,
}: {
  moduleCode: string;
  onClose: () => void;
  onCreated: (client: OAuthClient, secret: string | null) => void;
}): ReactElement {
  const [clientId, setClientId] = useState("");
  const [clientType, setClientType] = useState<OAuthClientType>("confidential");
  const [redirectUris, setRedirectUris] = useState("");
  const [postLogoutRedirectUris, setPostLogoutRedirectUris] = useState("");
  const [allowedScopes, setAllowedScopes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const input: CreateOAuthClientInput = {
        clientId,
        clientType,
        redirectUris: linesToArray(redirectUris),
        postLogoutRedirectUris: linesToArray(postLogoutRedirectUris),
        allowedScopes: linesToArray(allowedScopes),
      };
      const created = await clientsApi.create(moduleCode, input);
      const { clientSecret, ...client } = created;
      onCreated(client, clientSecret);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Nuevo cliente OAuth" onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Field label="Client ID" hint="Minúsculas, dígitos, guion y guion bajo; 3-64 caracteres.">
          {(props) => (
            <input {...props} required pattern="[a-z0-9][a-z0-9_-]{2,63}" value={clientId} onChange={(e) => setClientId(e.target.value)} />
          )}
        </Field>
        <Field label="Tipo de cliente">
          {(props) => (
            <select {...props} value={clientType} onChange={(e) => setClientType(e.target.value as OAuthClientType)}>
              <option value="confidential">Confidencial (con secreto)</option>
              <option value="public">Público (sin secreto)</option>
            </select>
          )}
        </Field>
        <Field label="Redirect URIs" hint="Una por línea.">
          {(props) => <textarea {...props} rows={3} value={redirectUris} onChange={(e) => setRedirectUris(e.target.value)} />}
        </Field>
        <Field label="Post-logout redirect URIs" hint="Una por línea, opcional.">
          {(props) => (
            <textarea {...props} rows={2} value={postLogoutRedirectUris} onChange={(e) => setPostLogoutRedirectUris(e.target.value)} />
          )}
        </Field>
        <Field label="Scopes permitidos" hint="Uno por línea, al menos uno.">
          {(props) => <textarea {...props} rows={2} required value={allowedScopes} onChange={(e) => setAllowedScopes(e.target.value)} />}
        </Field>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={submitting}>
            {submitting ? "Creando…" : "Crear cliente"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Corrección de un cliente ya registrado. Es la pantalla que faltaba: un redirect URI mal escrito
 * o una lista de scopes incompleta no se podían arreglar de ninguna forma, y la única salida era
 * registrar otro cliente con otro `clientId`.
 *
 * El `clientId` y el tipo no se editan: el primero es la identidad del cliente y el segundo decide
 * si tiene secreto, así que cambiarlos es registrar otro cliente.
 */
function EditClientModal({
  client,
  moduleCode,
  onClose,
  onUpdated,
}: {
  client: OAuthClient;
  moduleCode: string;
  onClose: () => void;
  onUpdated: (client: OAuthClient) => void;
}): ReactElement {
  const [redirectUris, setRedirectUris] = useState(client.redirectUris.join("\n"));
  const [postLogoutRedirectUris, setPostLogoutRedirectUris] = useState(
    client.postLogoutRedirectUris.join("\n"),
  );
  const [allowedScopes, setAllowedScopes] = useState(client.allowedScopes.join("\n"));
  const [status, setStatus] = useState<OAuthClientStatus>(client.status);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const input: UpdateOAuthClientInput = {
        allowedScopes: linesToArray(allowedScopes),
        postLogoutRedirectUris: linesToArray(postLogoutRedirectUris),
        redirectUris: linesToArray(redirectUris),
        status,
      };
      onUpdated(await clientsApi.update(moduleCode, client.clientId, input));
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={`Editar ${client.clientId}`} onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Field
          label="Redirect URIs"
          hint="Una por línea. Debe coincidir carácter por carácter con la que envía el módulo."
        >
          {(props) => (
            <textarea
              {...props}
              rows={3}
              value={redirectUris}
              onChange={(e) => setRedirectUris(e.target.value)}
            />
          )}
        </Field>
        <Field label="Post-logout redirect URIs" hint="Una por línea, opcional.">
          {(props) => (
            <textarea
              {...props}
              rows={2}
              value={postLogoutRedirectUris}
              onChange={(e) => setPostLogoutRedirectUris(e.target.value)}
            />
          )}
        </Field>
        <Field
          label="Scopes permitidos"
          hint="Uno por línea. Un scope que el módulo pida y no esté aquí hace que /oauth/authorize responda invalid_scope."
        >
          {(props) => (
            <textarea
              {...props}
              rows={5}
              required
              value={allowedScopes}
              onChange={(e) => setAllowedScopes(e.target.value)}
            />
          )}
        </Field>
        <Field label="Estado" hint="Revocado retira el cliente del flujo OIDC; se puede deshacer.">
          {(props) => (
            <select
              {...props}
              value={status}
              onChange={(e) => setStatus(e.target.value as OAuthClientStatus)}
            >
              <option value="active">Activo</option>
              <option value="revoked">Revocado</option>
            </select>
          )}
        </Field>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="modal-actions">
          <button type="button" className="button-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ModuleClients({ moduleCode }: { moduleCode: string }): ReactElement {
  const [items, setItems] = useState<OAuthClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [rotating, setRotating] = useState<string | null>(null);
  const [editing, setEditing] = useState<OAuthClient | null>(null);
  const [removing, setRemoving] = useState<OAuthClient | null>(null);
  const [revealSecret, setRevealSecret] = useState<{ title: string; secret: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await clientsApi.list(moduleCode);
      setItems(page.items);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setLoading(false);
    }
  }, [moduleCode]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleRotate(clientId: string): Promise<void> {
    setRotating(clientId);
    try {
      const created = await clientsApi.rotateSecret(moduleCode, clientId);
      const { clientSecret, ...client } = created;
      setItems((prev) => prev.map((c) => (c.clientId === client.clientId ? client : c)));
      if (clientSecret) setRevealSecret({ title: `Nuevo secreto de ${clientId}`, secret: clientSecret });
    } catch (err) {
      toast.error(toDisplayMessage(err));
    } finally {
      setRotating(null);
    }
  }

  const columns: DataTableColumn<OAuthClient>[] = [
    { key: "clientId", header: "Client ID", render: (c) => c.clientId },
    { key: "type", header: "Tipo", render: (c) => (c.clientType === "confidential" ? "Confidencial" : "Público") },
    { key: "status", header: "Estado", render: (c) => <Badge tone={toneForStatus(c.status)}>{c.status === "active" ? "Activo" : "Revocado"}</Badge> },
    { key: "scopes", header: "Scopes", render: (c) => c.allowedScopes.join(", ") },
    {
      key: "actions",
      header: "Acciones",
      render: (c) => (
        <>
          <button type="button" className="button-secondary button-small" onClick={() => setEditing(c)}>
            Editar
          </button>
          {c.clientType === "confidential" && (
            <button type="button" className="button-secondary button-small" disabled={rotating === c.clientId} onClick={() => void handleRotate(c.clientId)}>
              {rotating === c.clientId ? "Rotando…" : "Rotar secreto"}
            </button>
          )}
          <button type="button" className="button-secondary button-small" onClick={() => setRemoving(c)}>
            Eliminar
          </button>
        </>
      ),
    },
  ];

  return (
    <section>
      <div className="page-header">
        <h2>Clientes OAuth</h2>
        <button type="button" className="button-primary" onClick={() => setShowCreate(true)}>
          Nuevo cliente
        </button>
      </div>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      <DataTable columns={columns} rows={items} rowKey={(c) => c.clientId} emptyMessage={loading ? "Cargando…" : "No hay clientes OAuth."} />
      {showCreate && (
        <CreateClientModal
          moduleCode={moduleCode}
          onClose={() => setShowCreate(false)}
          onCreated={(client, secret) => {
            setShowCreate(false);
            setItems((prev) => [client, ...prev]);
            if (secret) setRevealSecret({ title: `Secreto de ${client.clientId}`, secret });
            else toast.success("Cliente OAuth creado.");
          }}
        />
      )}
      {editing && (
        <EditClientModal
          client={editing}
          moduleCode={moduleCode}
          onClose={() => setEditing(null)}
          onUpdated={(client) => {
            setEditing(null);
            setItems((prev) => prev.map((c) => (c.clientId === client.clientId ? client : c)));
            toast.success("Cliente OAuth actualizado.");
          }}
        />
      )}
      {revealSecret && <SecretReveal title={revealSecret.title} secret={revealSecret.secret} onClose={() => setRevealSecret(null)} />}
      {removing && (
        <ConfirmDialog
          title="Eliminar cliente OAuth"
          message={`¿Eliminar ${removing.clientId}? Solo se puede si nunca emitió credenciales; si ya se usó, el núcleo lo rechazará y habrá que revocarlo.`}
          confirmLabel="Eliminar"
          danger
          onCancel={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await clientsApi.remove(moduleCode, removing.clientId);
              setItems((prev) => prev.filter((c) => c.clientId !== removing.clientId));
              setRemoving(null);
              toast.success("Cliente OAuth eliminado.");
            } catch (err) {
              setRemoving(null);
              toast.error(toDisplayMessage(err));
            }
          }}
        />
      )}
    </section>
  );
}
